import type { DataLayerEvent } from '@marriott/mi-headless-utils';
import { ConsentOptionPayload } from '../../organisms/WebPromptConsent/WebPromptConsent.types';
import { pageViewACDLkeyGenerator, webPromptConsentACDLkeyGenerator } from './acdlKeyGenerator';
import { WEB_PROMPT_EXPERIENCE_VARIANTS, WEBPROMPT_EVENTS_STATE } from './constants/constants';
import { PromptData } from '../../organisms/WebPromptBanner/WebPromptBanner.types';

// Utility to map raw consent option to ConsentOptionPayload
type RawConsentOption = {
  method?: { code?: string } | string;
  type?: { code?: string } | string;
  subscribed?: boolean;
};

export function mapToConsentOptionPayload(opt: RawConsentOption): ConsentOptionPayload {
  return {
    method: { code: typeof opt.method === 'object' ? opt.method?.code || '' : (opt.method as string) || '' },
    type: { code: typeof opt.type === 'object' ? opt.type?.code || '' : (opt.type as string) || '' },
    subscribed: !!opt.subscribed,
  };
}

/**
 * Helper to push ACCOUNT_UPDATE datalayer event for WebPrompt flows.
 * @param updateDatalayer - The updateDatalayer function from useSession
 * @param options - Consent options array
 * @param phoneExists - 'Y' or 'N' for phone existence
 */

type dataLayerType = Record<string, unknown>;

export interface PushWebPromptDatalayerArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datalayer?: dataLayerType;
  updateDatalayer: (payload: DataLayerEvent) => void;
  options?: ConsentOptionPayload[];
  phoneExists: 'Y' | 'N' | 'E';
  eventName: string;
  modal?: boolean;
  isError?: boolean;
  isMobileUpdate?: boolean;
  isConsentUpdate?: boolean;
}

/**
 * Generic helper to push datalayer event for WebPrompt flows.
 * Includes user.settings_preferences only for ACCOUNT_UPDATE events.
 */

export function pushWebPromptDatalayer(args: PushWebPromptDatalayerArgs) {
  const {
    datalayer = {},
    updateDatalayer,
    options = [],
    phoneExists,
    eventName,
    modal = true,
    isError,
    isMobileUpdate = false,
    isConsentUpdate = false,
  } = args;
  const { emailConsent, smsConsent, postalConsent } = getConsentStatusFromOptions(options, isError);

  const allSubscribed = emailConsent === 'Y' && smsConsent === 'Y' && postalConsent === 'Y';

  const consentString = getConsentDataLayerString({
    phoneExists,
    emailConsent,
    smsConsent,
    postalConsent,
  });

  const experience_account_update =
    isMobileUpdate && allSubscribed
      ? WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_C
      : isMobileUpdate
      ? WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_A
      : isConsentUpdate && allSubscribed
      ? WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_B
      : getExperienceVariant(phoneExists, emailConsent, smsConsent, postalConsent);

  const experience = getExperienceVariant(phoneExists, emailConsent, smsConsent, postalConsent);

  let datalayerObj: DataLayerEvent = {
    event: eventName,
    ...pageViewACDLkeyGenerator({ datalayer, isModel: modal, experience }),
  };

  if (eventName === 'ACCOUNT_UPDATE') {
    datalayerObj = {
      event: eventName,
      ...webPromptConsentACDLkeyGenerator(consentString, experience_account_update, modal),
    };
  }
  updateDatalayer(datalayerObj);
}

export function getExperienceVariant(
  phoneExists: string,
  emailConsent: string,
  smsConsent: string,
  postalConsent: string
) {
  const phone = phoneExists === 'Y';
  const consents = [emailConsent, smsConsent, postalConsent];
  const allConsentsY = consents.every(c => c === 'Y');
  const hasConsentIssues = consents.some(c => c === 'N' || c === 'E');

  if (!phone && hasConsentIssues) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_A;
  if (phone && hasConsentIssues) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_B;
  if (!phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_C;
  if (phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_D;

  return '';
}

// Utility to generate consent datalayer string for WebPrompt flows

type ConsentStatus = {
  phoneExists: 'Y' | 'N' | 'E';
  emailConsent: 'Y' | 'N' | 'E';
  smsConsent: 'Y' | 'N' | 'E';
  postalConsent: 'Y' | 'N' | 'E';
};

export function getConsentDataLayerString({
  phoneExists,
  emailConsent,
  smsConsent,
  postalConsent,
}: ConsentStatus): string {
  return `Phone Exists:${phoneExists}|Email Consent:${emailConsent}|SMS Consent:${smsConsent}|Postal Consent:${postalConsent}`;
}

export function getConsentStatusFromOptions(
  communicationOptions: ConsentOptionPayload[] = [],
  isError: boolean = false
): Omit<ConsentStatus, 'phoneExists'> {
  let emailConsent: 'Y' | 'N' | 'E' = 'N';
  let smsConsent: 'Y' | 'N' | 'E' = 'N';
  let postalConsent: 'Y' | 'N' | 'E' = 'N';

  const resolveConsent = (subscribed?: boolean, isSelected?: boolean): 'Y' | 'N' | 'E' => {
    if (!subscribed && !isSelected) return 'N';
    return isError && isSelected && !subscribed ? 'E' : 'Y';
  };

  communicationOptions.forEach(opt => {
    if (
      (opt.method?.code === 'EML' && opt.type?.code === 'PRGRM') ||
      (opt.method?.code === 'EMAIL' && opt.type?.code === 'MRKT')
    ) {
      emailConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }

    if (opt.method?.code === 'SMS' && opt.type?.code === 'MRKT') {
      smsConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }

    if (opt.method?.code === 'POST' && opt.type?.code === 'GNRL') {
      postalConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }
  });

  return { emailConsent, smsConsent, postalConsent };
}

// Helper to determine if phone exists (type MT only)
interface Customer {
  contactInformation?: {
    phones?: Array<{ number?: string; type?: { code?: string } }>;
  };
}

export function getPhoneExistsFromCustomer(customer: Customer): 'Y' | 'N' {
  const phones = customer?.contactInformation?.phones || [];
  return phones.some(phone => !!phone?.number && phone?.type?.code === 'MT') ? 'Y' : 'N';
}

// Manage (get/set/remove) sessionStorage state for web prompts
type ActionType = 'get' | 'set' | 'remove';
export const handleWebPromptState = (action: ActionType, key: string, value?: boolean): boolean | null => {
  if (typeof window === 'undefined') return null;
  const state = JSON.parse(sessionStorage.getItem(WEBPROMPT_EVENTS_STATE) || '{}');
  if (action === 'get') return state[key];
  if (action === 'set') state[key] = value ?? true;
  if (action === 'remove') delete state[key];
  sessionStorage.setItem(WEBPROMPT_EVENTS_STATE, JSON.stringify(state));
  return true;
};

export const storageHandler = <T>(key: string, action: ActionType, value?: T): T | null => {
  if (typeof window === 'undefined') return null;
  if (action === 'get') {
    const data = localStorage.getItem(key);
    return data ? (JSON.parse(data) as T) : null;
  }
  if (action === 'set') {
    localStorage.setItem(key, JSON.stringify(value));
    return null;
  }
  if (action === 'remove') {
    localStorage.removeItem(key);
    return null;
  }
  return null;
};

const getDurationInMs = (value?: string): number => {
  if (!value) return 0;

  const match = value.match(/^(\d+)([mhd])$/i);
  if (!match) return Number(value) * 24 * 60 * 60 * 1000; // fallback (day format)

  const [, num, unit] = match;
  const n = Number(num);

  switch (unit.toLowerCase()) {
    case 'm':
      return n * 60 * 1000; // minutes
    case 'h':
      return n * 60 * 60 * 1000; // hours
    case 'd':
      return n * 24 * 60 * 60 * 1000; // days
    default:
      return 0;
  }
};

export const getNextRenderablePrompt = (prompts: PromptData[], hideDuration?: string): PromptData | null => {
  const now = Date.now();
  const duration = getDurationInMs(hideDuration);

  // Sort by variation (V1, V2, V3...)
  const sorted = [...prompts].sort((a, b) => {
    const numA = parseInt(a.promptVariation?.replace('V', '') || '0', 10);
    const numB = parseInt(b.promptVariation?.replace('V', '') || '0', 10);
    return numA - numB;
  });

  return (
    sorted.find(item => {
      // Case 1: no renderState OR explicitly enabled
      if (!('renderState' in item) || item.renderState === 'enable') {
        return true;
      }

      // Case 2: disabled → check time condition
      if (item.renderState === 'disabled' && item.disabledAt) {
        const disabledTime = new Date(item.disabledAt).getTime();
        return disabledTime + duration <= now;
      }

      return false;
    }) || null
  );
};

export const getNextPromptFromKey = (
  prompts: PromptData[],
  currentKey: string,
  hideDuration?: string
): PromptData | null => {
  const current = prompts.find(p => p.promptKey === currentKey);
  if (!current) return null;
  const currentVar = parseInt(current.promptVariation?.replace('V', '') || '0', 10);
  // only take NEXT variations
  const nextPrompts = prompts.filter(p => parseInt(p.promptVariation?.replace('V', '') || '0', 10) > currentVar);
  return getNextRenderablePrompt(nextPrompts, hideDuration);
};

export const updatePromptState = (
  data: PromptData[],
  targetKey: string,
  state: 'enable' | 'disabled',
  dateTime?: string
): PromptData[] => {
  return data.map(item =>
    item.promptKey === targetKey
      ? {
          ...item,
          renderState: state,
          ...(state === 'disabled' ? { disabledAt: dateTime } : { disabledAt: undefined }),
        }
      : item
  );
};
